import { makeActions } from '@redux/crudCreator/actions';
import {
  PRIMARY_KEY,
  convertRequestParams,
  convertResponseData,
} from '@redux/crudCreator/dataProvider';
import { apiWrapper } from '@redux/crudCreator/reduxUtils';
import { GetAllPayloadType } from '@redux/crudCreator/types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getAllApi, getDataByIdApi } from 'api/crud';
import { flatten, uniq } from 'lodash';

export const MODEL_NAME = 'productVariants';
export const productVariantsActions = makeActions(
  MODEL_NAME,
  'id',
  'product-variants',
);

export const getAllProductVariants = productVariantsActions.getAll;
export const editProductVariants = productVariantsActions.edit;
export const createProductVariants = productVariantsActions.create;
export const getByIdProductVariants = productVariantsActions.getDataById;

export const getProductVariantDetail = createAsyncThunk(
  'productVariants/getProductVariantDetail',
  async (payload: GetAllPayloadType, thunkAPI) => {
    const { data, options = { extraParams: {}, isRequestApi: true } } = payload;
    try {
      const response = await apiWrapper(
        {},
        getDataByIdApi,
        'product-variants',
        data[PRIMARY_KEY],
        options.suffix,
        options.extraParams,
      );
      const result = convertResponseData('GET_BY_ID', response, {
        primaryKey: 'id',
      });

      const convertRequestAttributeValues = convertRequestParams('GET_ALL', {
        limit: 100,
        offset: 0,
        join: ['detail'],
        filters: [
          {
            condition: 'and',
            key: 'id',
            operator: 'is',
            value: result?.attributeValueIds,
          },
        ],
      });

      const convertRequestAttributes = (attributeValues) => {
        return convertRequestParams('GET_ALL', {
          limit: 100,
          offset: 0,
          join: ['detail'],
          filters: [
            {
              condition: 'and',
              key: 'id',
              operator: 'is',
              value: attributeValues,
            },
          ],
        });
      };

      const attributesRes = async (values) => {
        const res = await apiWrapper(
          {},
          getAllApi,
          'product-attributes',
          convertRequestAttributes(values),
        ).then((r) => ({
          attributes: r?.data || [],
        }));

        return res;
      };

      const res = await Promise.all([
        ...(result?.attributeValueIds?.length > 0
          ? [
              apiWrapper(
                {},
                getAllApi,
                'product-attribute-values',
                convertRequestAttributeValues,
              ).then(async (e) => {
                const attributes = await attributesRes(
                  e?.data?.map((e) => e?.attributeId),
                );
                return {
                  attributes: attributes?.attributes || [],
                  attributeValues: e?.data || [],
                };
              }),
            ]
          : []),
      ]);

      if (result) {
        return {
          data: {
            ...result,
            ...res?.reduce(
              (e, key) => ({
                ...e,
                ...key,
              }),
              {},
            ),
          },
        };
      }
      return thunkAPI.rejectWithValue({ data: result, options });
    } catch (error) {
      return thunkAPI.rejectWithValue({ data, options });
    }
  },
);

export const getAllProductVariantByProductId = createAsyncThunk(
  'productVariants/getAllProductVariantByProductId',
  async (payload: GetAllPayloadType, thunkAPI) => {
    const { data = {}, options = {} } = payload;
    const { pageSize, page, includes, filters } =
      thunkAPI.getState()[MODEL_NAME];
    try {
      const convertRequest = convertRequestParams('GET_ALL', {
        limit: pageSize,
        offset: pageSize * (page - 1),
        filters,
        includes,
        ...data,
      });
      const response = await apiWrapper(
        { isShowProgress: options.isShowProgress },
        getAllApi,
        'product-variants',
        convertRequest,
      );

      const listAttributeValueIds = uniq(
        flatten(
          response?.data
            ?.map((item) => item?.attributeValueIds)
            .filter((e) => e),
        ),
      ) as string[];

      const convertRequestAttributeValues = convertRequestParams('GET_ALL', {
        limit: 100,
        offset: 0,
        join: ['detail'],
        filters: listAttributeValueIds?.length > 0 && [
          {
            condition: 'and',
            key: 'id',
            operator: 'is',
            value: listAttributeValueIds || undefined,
          },
        ],
      });

      const attributeValuesRes =
        listAttributeValueIds?.length > 0 &&
        (await apiWrapper(
          { isShowProgress: options.isShowProgress },
          getAllApi,
          'product-attribute-values',
          convertRequestAttributeValues,
        ));

      const formatResponse = response?.data?.map((e) => {
        const attributeValues = attributeValuesRes?.data?.filter((item) =>
          e?.attributeValueIds?.includes(item.id),
        );
        return {
          ...e,
          attributeValues,
        };
      });

      const result = convertResponseData(
        'GET_ALL',
        { ...response, data: formatResponse },
        {
          primaryKey: PRIMARY_KEY,
        },
      );

      if (result.data) {
        return {
          data: {
            numberOfPages: Math.round(result.total / pageSize),
            ...result,
          },
          options,
        };
      }

      return thunkAPI.rejectWithValue({ data: response, options });
    } catch (error) {
      return thunkAPI.rejectWithValue({ data, options });
    }
  },
);
